"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";

/**
 * Global site footer. Mounted from `app/layout.tsx` so it appears on every
 * route. Phearl LLC operates as B-Laundry (DBA) — same business — so we credit
 * the operating entity without implying a separate trademark holder.
 *
 * Suppressed on the admin console (/admin), which renders its own compact
 * collapsible AdminFooter instead.
 */
export function SiteFooter() {
  const pathname = usePathname();
  const year = new Date().getFullYear();
  const [contentLinks,setContentLinks]=useState<{slug:string;title:string}[]>([]);
  useEffect(()=>{
    if(pathname?.startsWith("/admin")||pathname?.startsWith("/ops/run"))return;
    const abort=new AbortController();
    void fetch("/api/content",{signal:abort.signal}).then(async response=>{if(!response.ok)return;const body=await response.json();if(Array.isArray(body.pages))setContentLinks(body.pages);}).catch(()=>{});
    return ()=>abort.abort();
  },[pathname]);

  if (pathname?.startsWith("/admin") || pathname?.startsWith("/ops/run")) return null;

  const columns = [
    {
      heading: "Services",
      links: [
        { label: "Schedule a pickup", href: "/pickup" },
        { label: "Pricing", href: "/#pricing" },
        { label: "Dry cleaning", href: "/dry-cleaning" },
        { label: "FAQ", href: "/#faq" },
      ],
    },
    {
      heading: "Account",
      links: [
        { label: "My account", href: "/account" },
        { label: "Sign in", href: "/signin" },
      ],
    },
    {
      heading: "Legal",
      links: [
        { label: "Terms of Service", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Accessibility", href: "/accessibility" },
      ],
    },
  ];

  if(contentLinks.length)columns.push({heading:"More",links:contentLinks.map(page=>({label:page.title,href:`/pages/${page.slug}`}))});

  return (
    <footer className="w-full bg-[#1F1B2E] text-white">
      <div className="mx-auto w-full max-w-6xl px-6 py-14 sm:px-8">
        <div className="flex flex-col gap-12 lg:flex-row lg:justify-between lg:gap-8">
          {/* Brand */}
          <div className="max-w-xs">
            <div className="flex items-center gap-3">
              <Image
                src="/assets/logo.svg"
                alt="B-Laundry logo"
                width={40}
                height={40}
                className="h-9 w-auto"
              />
              <div className="flex flex-col leading-tight">
                <span translate="no" className="notranslate text-xl font-semibold">
                  B-Laundry
                </span>
                <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/60">
                  In partnership with <span className="notranslate">Fold</span>
                </span>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/75">
              Door-to-door laundry and dry cleaning for UCLA students. Hand us
              your bag, get it back fresh.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.instagram.com/b_laundry_la"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="B-Laundry on Instagram"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-white/40 hover:text-white"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/b-laundry/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="B-Laundry on LinkedIn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-white/40 hover:text-white"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns */}
          <nav
            aria-label="Footer"
            className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:gap-16"
          >
            {columns.map((col) => (
              <div key={col.heading}>
                <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/75">
                  {col.heading}
                </h2>
                <ul className="mt-4 space-y-2.5 text-sm">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className="text-white/75 transition-colors hover:text-white"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/75 sm:flex-row sm:items-center">
          <span>
            &copy; {year} <span className="notranslate">B-Laundry</span>. All rights reserved.
          </span>
          <span>
            A service of Phearl LLC &middot;{" "}
            <a
              href="mailto:support@b-laundry.co"
              className="font-medium text-white/75 transition-colors hover:text-white"
            >
              support@b-laundry.co
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
